import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/utils/api';

const API = API_URL;

const TenantContext = createContext();

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const [effectiveTenantId, setEffectiveTenantId] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPlatformAdmin = user?.role === 'platform_superadmin';
  const isSupervisor = user?.role === 'tenant_supervisor';

  const fetchTenants = useCallback(async () => {
    setError(null);
    
    // If user has a valid tenantId, use it directly
    if (user?.tenantId) {
      setEffectiveTenantId(user.tenantId);
      setLoading(false);
      return;
    }

    // For platform_superadmin, fetch all tenants
    if (isPlatformAdmin) {
      try {
        const token = localStorage.getItem('punchtm-token');
        const response = await axios.get(`${API}/tenants`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTenants(response.data);
        
        // Set first tenant as effective tenant by default
        if (response.data.length > 0) {
          const firstTenant = response.data[0];
          setEffectiveTenantId(firstTenant.id);
          setSelectedTenant(firstTenant);
        }
      } catch (err) {
        console.error('Failed to fetch tenants:', err);
        setError('Impossible de charger les tenants');
      } finally {
        setLoading(false);
      }
      return;
    }

    // For non-platform users without tenantId (misconfigured) - show error
    if (user && !user.tenantId && user.role !== 'platform_superadmin') {
      console.error('User has no tenantId but is not platform_superadmin:', user.email, user.role);
      setError(`Configuration utilisateur invalide: ${user.email} n'a pas de tenant assigné. Contactez l'administrateur.`);
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [isPlatformAdmin, user]);

  useEffect(() => {
    if (user) {
      fetchTenants();
    } else {
      setLoading(false);
    }
  }, [user, fetchTenants]);

  const selectTenant = useCallback((tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    setEffectiveTenantId(tenantId);
    setSelectedTenant(tenant || null);
  }, [tenants]);

  // Expose supervisor's shiftId for filtering
  const supervisorShiftId = isSupervisor ? user?.shiftId : null;

  const value = {
    effectiveTenantId,
    selectedTenant,
    tenants,
    loading,
    error,
    selectTenant,
    isPlatformAdmin,
    isSupervisor,
    supervisorShiftId,
    refetchTenants: fetchTenants
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export default TenantContext;
